import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "@/services/authService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Mail, Lock, Loader2, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import liveStayLogo from "@/assets/live-stay-logo.png";

const AdminLogin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);

  // Validação de email
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !senha) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos.",
        variant: "destructive",
      });
      return;
    }

    if (!validateEmail(email)) {
      toast({
        title: "Erro",
        description: "Por favor, insira um email válido.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await authService.login({ email, password: senha });

      if (!response || !response.user) {
        toast({
          title: "Erro no login",
          description: "Credenciais inválidas. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      if (response.user.user_type !== "admin") {
        toast({
          title: "Acesso Negado",
          description: "Esta área é exclusiva para administradores.",
          variant: "destructive",
        });
        // Limpar token se não for admin
        localStorage.removeItem("token");
        localStorage.removeItem("authToken");
        return;
      }

      toast({
        title: "Login realizado com sucesso!",
        description: "Bem-vindo à área administrativa.",
      });

      navigate("/admin/dashboard");
    } catch (error: any) {
      console.error("Erro no login:", error);
      toast({
        title: "Erro no login",
        description: error.message || "Credenciais inválidas. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Painel Esquerdo - Decoração com tema de viagens/férias */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-500 via-blue-400 to-cyan-400 relative overflow-hidden">
        {/* Background pattern de ondas e viagens */}
        <div className="absolute inset-0 opacity-20">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path fill="rgba(255,255,255,0.3)" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,165.3C1248,149,1344,107,1392,85.3L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
          <svg className="absolute bottom-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path fill="rgba(255,255,255,0.2)" d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,213.3C672,224,768,224,864,208C960,192,1056,160,1152,154.7C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
        
        {/* Elementos decorativos de viagem */}
        <div className="absolute inset-0">
          {/* Sol/Palmeiras decorativos */}
          <div className="absolute top-10 right-10 w-32 h-32 bg-yellow-300/20 rounded-full blur-2xl" />
          <div className="absolute bottom-20 left-10 w-24 h-24 bg-white/10 rounded-full blur-xl" />
          <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-cyan-200/20 rounded-full blur-lg" />
        </div>
        
        {/* Overlay escuro sutil */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
        
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
          {/* Logo Live Stay */}
          <div className="mb-8">
            <img 
              src={liveStayLogo} 
              alt="Live Stay" 
              className="h-24 w-auto drop-shadow-2xl"
            />
          </div>
          
          <div className="text-center space-y-4 max-w-md">
            <h1 className="text-4xl font-bold leading-tight drop-shadow-lg">
              Bem-vindo ao Painel Administrativo
            </h1>
            <p className="text-lg text-white/95 leading-relaxed drop-shadow-md">
              Gerencie seu sistema de forma eficiente e segura com nossa plataforma administrativa completa.
            </p>
          </div>

          <div className="mt-12 flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-white/90 animate-pulse" />
              <span className="text-sm text-white/95 font-medium">Sistema Seguro</span>
            </div>
            <div className="w-px h-6 bg-white/40" />
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-white/95" />
              <span className="text-sm text-white/95 font-medium">Acesso Restrito</span>
            </div>
          </div>
        </div>

        {/* Decoração de fundo - elementos flutuantes */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-300/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-yellow-200/10 rounded-full blur-2xl" />
      </div>

      {/* Painel Direito - Formulário */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Header Mobile */}
          <div className="lg:hidden text-center space-y-4">
            <div className="flex justify-center">
              <img 
                src={liveStayLogo} 
                alt="Live Stay" 
                className="h-16 w-auto"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Área Administrativa</h1>
              <p className="text-sm text-muted-foreground mt-2">
                Faça login para acessar o painel
              </p>
            </div>
          </div>

          {/* Card de Login */}
          <div className="bg-card border border-border rounded-lg shadow-lg p-6 sm:p-8 space-y-6">
            {/* Header Desktop */}
            <div className="hidden lg:block space-y-2">
              <h2 className="text-2xl font-bold text-foreground">Bem-vindo de volta! 👋</h2>
              <p className="text-sm text-muted-foreground">
                Por favor, faça login na sua conta e comece a aventura
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              {/* Campo Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Campo Senha */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="senha" className="text-sm font-medium">
                    Senha
                  </Label>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="senha"
                    type="password"
                    placeholder="••••••••"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    className="pl-10 h-11"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Botão de Login */}
              <Button
                type="submit"
                className="w-full h-11 text-base font-medium"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Entrando...
                  </>
                ) : (
                  <>
                    Entrar
                    <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
                  </>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            {/* Link para voltar */}
            <div className="text-center">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar ao site
              </Button>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-muted-foreground">
            <p>
              © {new Date().getFullYear()} Live Stay. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
